import { Link, useNavigate } from '@tanstack/react-router'
import { type ReactElement, useEffect, useState } from 'react'

import { AuthLayout } from '@/components/auth/auth-layout'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useParametroDaBusca } from '@/lib/rota'
import { renovarAccessToken } from '@/lib/sessao'
import { useAuthStore } from '@/stores/auth-store'

/**
 * Retorno do login social.
 *
 * O painel NÃO recebe `code` nem `state` do provedor: quem trata o callback é a
 * API, que valida o `state`, faz a troca do código pelo token no servidor,
 * vincula a identidade e grava o cookie httpOnly de refresh. Aqui só chega o
 * desfecho — e o painel converte esse cookie em sessão pedindo um access token.
 *
 * Fazer a troca no navegador exigiria o segredo do cliente OAuth dentro do
 * bundle, o que é exatamente o que a invariante nº 8 proíbe.
 */

const MENSAGENS: Readonly<Record<string, string>> = {
  CONTA_LOCAL_EXISTENTE:
    'Já existe uma conta com esse e-mail e senha própria. Entre com a senha e, depois, vincule o provedor no seu perfil — vincular às cegas seria um jeito de tomar a conta de outra pessoa.',
  EMAIL_NAO_VERIFICADO:
    'O provedor não confirmou que esse e-mail é seu. Sem e-mail verificado não é possível vincular a conta.',
  ACESSO_NEGADO: 'Você cancelou a autorização no provedor. Nada foi alterado.',
  TOKEN_INVALIDO: 'A autorização expirou no meio do caminho. Tente entrar novamente.',
}

export function RetornoOAuthPage(): ReactElement {
  const navigate = useNavigate()
  const status = useParametroDaBusca('status')
  const codigo = useParametroDaBusca('codigo')
  const usuario = useAuthStore((estado) => estado.usuario)
  const [falhou, setFalhou] = useState(status !== 'ok')

  useEffect(() => {
    // `ativo` evita atualizar estado depois que a tela saiu — o efeito roda
    // duas vezes em desenvolvimento por causa do StrictMode.
    let ativo = true

    if (status === 'ok') {
      void renovarAccessToken().then((token) => {
        if (!ativo) {
          return
        }

        // É "a renovação não devolveu token", não comparação de segredo.
        // eslint-disable-next-line security/detect-possible-timing-attacks
        if (token === null) {
          setFalhou(true)
          return
        }

        const precisaTotp = useAuthStore.getState().usuario?.precisaConfigurarTotp ?? false
        void navigate({ to: precisaTotp ? '/2fa/configurar' : '/' })
      })
    } else {
      setFalhou(true)
    }

    return () => {
      ativo = false
    }
  }, [navigate, status])

  if (falhou) {
    const mensagem =
      codigo === null
        ? 'Não foi possível concluir a entrada pelo provedor.'
        : (MENSAGENS[codigo] ?? 'Não foi possível concluir a entrada pelo provedor.')

    return (
      <AuthLayout
        titulo="Não deu para entrar"
        rodape={
          <Link to="/login" className="text-brand text-sm underline-offset-4 hover:underline">
            Voltar para o login
          </Link>
        }
      >
        <Alert variant="danger">
          <AlertTitle>Login social não concluído</AlertTitle>
          <AlertDescription>{mensagem}</AlertDescription>
        </Alert>

        <Button asChild className="w-full">
          <Link to="/login">Entrar com e-mail e senha</Link>
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout titulo="Concluindo a entrada" descricao={usuario?.email}>
      <LoadingState rotulo="Validando a autorização do provedor…" linhas={1} />
    </AuthLayout>
  )
}
